import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TableViewIcon from '@mui/icons-material/TableView';
import CalculateIcon from '@mui/icons-material/Calculate';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import BlockIcon from '@mui/icons-material/Block';

const drawerWidth = 240;

const navItems = [
  { title: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { title: 'Upload Data', path: '/upload-data', icon: <UploadFileIcon /> },
  { title: 'Rate Table', path: '/rate-table', icon: <TableViewIcon /> },
  { title: 'Results', path: '/results', icon: <CalculateIcon /> },
  { title: 'Projections', path: '/projections', icon: <AssessmentIcon /> },
  { title: 'Service Types', path: '/service-types', icon: <SettingsIcon /> },
  { title: 'Non-Payment Orders', path: '/non-payment-orders', icon: <BlockIcon /> },
];

function Navigation() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [rateTableAnchorEl, setRateTableAnchorEl] = React.useState(null);
  const [uploadAnchorEl, setUploadAnchorEl] = React.useState(null);
  const { user, signOut } = useAuth();
  
  const rateTableMenuOpen = Boolean(rateTableAnchorEl);
  const uploadMenuOpen = Boolean(uploadAnchorEl);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleRateTableClick = (event) => {
    if (isMobile) {
      // On mobile, navigate directly
      return;
    }
    setRateTableAnchorEl(event.currentTarget);
  };
  
  const handleUploadClick = (event) => {
    if (isMobile) {
      // On mobile, navigate directly
      return;
    }
    setUploadAnchorEl(event.currentTarget);
  };
  
  const handleRateTableClose = () => {
    setRateTableAnchorEl(null);
  };
  
  const handleUploadClose = () => {
    setUploadAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
  };

  const isUploadActive = () => {
    return location.pathname === '/upload-data' || location.pathname === '/upload-refusal';
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <Logo height={70} showText={false} />
      </Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Contractor Payment Tools
      </Typography>
      <Divider />
      <List>
        {/* Dashboard item */}
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/"
            selected={location.pathname === '/'}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        {/* Upload Data items */}
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/upload-data"
            selected={location.pathname === '/upload-data'}
          >
            <ListItemIcon>
              <CheckCircleIcon />
            </ListItemIcon>
            <ListItemText primary="Upload Completed Work" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/upload-refusal"
            selected={location.pathname === '/upload-refusal'}
          >
            <ListItemIcon>
              <DoDisturbAltIcon />
            </ListItemIcon>
            <ListItemText primary="Upload Refusal Work" />
          </ListItemButton>
        </ListItem>

        {/* Rate Table item */}
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/rate-table"
            selected={location.pathname === '/rate-table'}
          >
            <ListItemIcon>
              <TableViewIcon />
            </ListItemIcon>
            <ListItemText primary="Rate Table" />
          </ListItemButton>
        </ListItem>

        {/* Results item */}
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/results"
            selected={location.pathname === '/results'}
          >
            <ListItemIcon>
              <CalculateIcon />
            </ListItemIcon>
            <ListItemText primary="Results" />
          </ListItemButton>
        </ListItem>

        {/* Projections item */}
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/projections"
            selected={location.pathname === '/projections'}
          >
            <ListItemIcon>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText primary="Projections" />
          </ListItemButton>
        </ListItem>

        {/* Service Types item */}
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/service-types"
            selected={location.pathname === '/service-types'}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Service Types" />
          </ListItemButton>
        </ListItem>

        {/* Non-Payment Orders item */}
        <ListItem disablePadding>
          <ListItemButton 
            component={Link} 
            to="/non-payment-orders"
            selected={location.pathname === '/non-payment-orders'}
          >
            <ListItemIcon>
              <BlockIcon />
            </ListItemIcon>
            <ListItemText primary="Non-Payment Orders" />
          </ListItemButton>
        </ListItem>

        {user && (
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar sx={{ minHeight: { xs: '80px', sm: '90px' } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}>
            <Logo height={80} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            {navItems.map((item) => {
              // Handle Rate Table dropdown
              if (item.title === 'Rate Table') {
                return (
                  <Box key={item.title}>
                    <Button
                      id="rate-table-button"
                      aria-controls={rateTableMenuOpen ? 'rate-table-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={rateTableMenuOpen ? 'true' : undefined}
                      onClick={handleRateTableClick}
                      endIcon={<KeyboardArrowDownIcon />}
                      component={Link}
                      to={item.path}
                      color="inherit"
                      sx={{ 
                        mx: 1,
                        backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
                      }}
                    >
                      {item.title}
                    </Button>
                    <Menu
                      id="rate-table-menu"
                      anchorEl={rateTableAnchorEl}
                      open={rateTableMenuOpen}
                      onClose={handleRateTableClose}
                      MenuListProps={{
                        'aria-labelledby': 'rate-table-button',
                      }}
                    >
                      <MenuItem 
                        component={Link} 
                        to="/rate-table" 
                        onClick={handleRateTableClose}
                      >
                        <ListItemIcon>
                          <TableViewIcon fontSize="small" />
                        </ListItemIcon>
                        View Rate Table
                      </MenuItem>
                      <MenuItem 
                        component={Link} 
                        to="/provider/add" 
                        onClick={handleRateTableClose}
                      >
                        <ListItemIcon>
                          <PersonIcon fontSize="small" />
                        </ListItemIcon>
                        Add New Provider
                      </MenuItem>
                    </Menu>
                  </Box>
                );
              }
              
              // Handle Upload Data dropdown
              if (item.title === 'Upload Data') {
                return (
                  <Box key={item.title}>
                    <Button
                      id="upload-button"
                      aria-controls={uploadMenuOpen ? 'upload-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={uploadMenuOpen ? 'true' : undefined}
                      onClick={handleUploadClick}
                      endIcon={<KeyboardArrowDownIcon />}
                      color="inherit"
                      sx={{ 
                        mx: 1,
                        backgroundColor: isUploadActive() ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
                      }}
                    >
                      {item.title}
                    </Button>
                    <Menu
                      id="upload-menu"
                      anchorEl={uploadAnchorEl}
                      open={uploadMenuOpen}
                      onClose={handleUploadClose}
                      MenuListProps={{
                        'aria-labelledby': 'upload-button',
                      }}
                    >
                      <MenuItem 
                        component={Link} 
                        to="/upload-data" 
                        onClick={handleUploadClose}
                      >
                        <ListItemIcon>
                          <CheckCircleIcon fontSize="small" />
                        </ListItemIcon>
                        Upload Completed Work
                      </MenuItem>
                      <MenuItem 
                        component={Link} 
                        to="/upload-refusal" 
                        onClick={handleUploadClose}
                      >
                        <ListItemIcon>
                          <DoDisturbAltIcon fontSize="small" />
                        </ListItemIcon>
                        Upload Refusal Work
                      </MenuItem>
                    </Menu>
                  </Box>
                );
              }
              
              // Regular navigation items
              return (
                <Button
                  key={item.title}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  sx={{ 
                    mx: 1,
                    backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.15)' : 'transparent'
                  }}
                >
                  {item.title}
                </Button>
              );
            })}
            {user && (
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                sx={{ ml: 2 }}
              >
                Logout
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default Navigation; 